import { useEffect, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import { CAPABILITIES } from "../lib/capabilities";
import type { PermissionGroup, User } from "../lib/types";
import * as work from "../lib/workClient";

/* Roles & Permissions. An editable matrix of permission groups × capabilities
 * — toggle which sections each group can reach — plus a roster that assigns
 * each teammate to a group. Capabilities gate the nav and the routes, so a
 * group without "settings"/"governance"/etc. never sees those config menus. */

export default function Permissions() {
  const { ws, me } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [users, setUsers] = useState<User[]>(ws.users);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    work.loadPermissionGroups(ws).then((r) => {
      setMode(r.mode);
      setGroups(r.groups);
      setUsers(r.mode === "api" ? ws.users : work.applyUserGroupOverrides(ws));
    });
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ws]);

  const toggleCap = async (g: PermissionGroup, capId: string) => {
    const has = g.capabilities.includes(capId);
    const capabilities = has ? g.capabilities.filter((c) => c !== capId) : [...g.capabilities, capId];
    setGroups((prev) => prev.map((x) => x.id === g.id ? { ...x, capabilities } : x)); // optimistic
    await work.updatePermissionGroup(mode, ws, g.id, { capabilities });
  };

  const rename = async (g: PermissionGroup, name: string) => {
    setGroups((prev) => prev.map((x) => x.id === g.id ? { ...x, name } : x));
    await work.updatePermissionGroup(mode, ws, g.id, { name });
  };

  const remove = async (g: PermissionGroup) => {
    if (g.system) return;
    if (!confirm(`Delete the "${g.name}" group? Members will be reassigned.`)) return;
    await work.deletePermissionGroup(mode, ws, g.id);
    refresh();
  };

  const addGroup = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    await work.createPermissionGroup(mode, ws, { name, capabilities: ["customers", "insights", "execution", "revenue"] });
    setBusy(false); setNewName(""); setCreating(false); refresh();
  };

  const assign = async (u: User, groupId: string) => {
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, groupId } : x));
    await work.setUserGroup(mode, ws, u.id, groupId);
  };

  const memberCount = (id: string) => users.filter((u) => u.groupId === id).length;

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Configure</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Roles &amp; permissions</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Permission groups bundle the capabilities a teammate can reach. Toggle a capability off and that group stops seeing its section in the nav — and can't open its pages directly. Configuration lives behind the admin capabilities, so CSMs get a clean, focused workspace.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <span className={`mode-pill ${mode === "api" ? "live" : ""}`}><i />{mode === "api" ? "Live API" : "Local demo"}</span>
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}><Icon name="plus" size={14} /> New group</button>
        </div>
      </div>

      {creating && (
        <div className="card mb-16">
          <div className="card__bd row" style={{ gap: 8 }}>
            <input className="inp" style={{ flex: 1 }} autoFocus placeholder="Group name (e.g. Onboarding Specialist)" value={newName}
              onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} />
            <button className="btn btn--primary btn--sm" disabled={!newName.trim() || busy} onClick={addGroup}>{busy ? "Adding…" : "Add group"}</button>
            <button className="btn btn--sm" onClick={() => { setCreating(false); setNewName(""); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* capability matrix */}
      <div className="card mb-24" style={{ overflowX: "auto" }}>
        <table className="perm-matrix">
          <thead>
            <tr>
              <th className="perm-matrix__cap">Capability</th>
              {groups.map((g) => (
                <th key={g.id} className="perm-matrix__grp">
                  <input className="perm-matrix__name" value={g.name} disabled={g.system}
                    onChange={(e) => rename(g, e.target.value)} title={g.system ? "Built-in group" : "Rename group"} />
                  <div className="perm-matrix__meta">
                    {memberCount(g.id)} member{memberCount(g.id) === 1 ? "" : "s"}
                    {g.system ? <span className="tag" style={{ marginLeft: 6 }}>Built-in</span> :
                      <button className="linklike" style={{ marginLeft: 6 }} onClick={() => remove(g)}>Delete</button>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((cap) => (
              <tr key={cap.id}>
                <td className="perm-matrix__cap">
                  <div className="row" style={{ gap: 7, alignItems: "center" }}>
                    <b style={{ fontSize: 13 }}>{cap.label}</b>
                    {cap.admin && <span className="tag" title="Admin / configuration surface"><Icon name="lock" size={9} /> Admin</span>}
                  </div>
                  <div className="faint" style={{ fontSize: 11.5, marginTop: 2 }}>{cap.description}</div>
                </td>
                {groups.map((g) => {
                  const on = g.capabilities.includes(cap.id);
                  return (
                    <td key={g.id} className="perm-matrix__cell">
                      <button className={`perm-toggle ${on ? "on" : ""}`} onClick={() => toggleCap(g, cap.id)}
                        aria-pressed={on} title={on ? `${g.name} can access ${cap.label}` : `${g.name} can't access ${cap.label}`}>
                        {on && <Icon name="check" size={13} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* roster: assign each teammate to a group */}
      <h2 style={{ fontFamily: "var(--serif)", fontSize: 20, marginBottom: 12 }}>Team members</h2>
      <div className="card">
        <div className="card__bd" style={{ display: "grid", gap: 2 }}>
          {users.length === 0 ? <Empty>No teammates yet.</Empty> : users.map((u) => (
            <div key={u.id} className="row between perm-roster__row">
              <div className="row" style={{ gap: 11, alignItems: "center" }}>
                <Avatar name={u.name} />
                <div>
                  <div className="row" style={{ gap: 7, alignItems: "center" }}>
                    <b style={{ fontSize: 14 }}>{u.name}</b>
                    {u.id === me.id && <span className="tag">You</span>}
                  </div>
                  <div className="faint" style={{ fontSize: 12 }}>{u.role}</div>
                </div>
              </div>
              <select className="inp" style={{ width: 200 }} value={u.groupId ?? ""} onChange={(e) => assign(u, e.target.value)}>
                {!u.groupId && <option value="">— Unassigned —</option>}
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
